import { useEffect, useRef } from "react";

/**
 * 把元素进入视口的进度写入 CSS 变量 `--enter`：
 * 元素顶边位于视口底部时为 0，到达视口 `endRatio` 处时为 1。
 */
export function useScrollEnter<T extends HTMLElement>(endRatio = 0.35) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.style.setProperty("--enter", "1");
      return;
    }
    let frame = 0;
    let active = false;
    const measure = () => {
      frame = 0;
      const top = element.getBoundingClientRect().top;
      const viewport = window.innerHeight;
      const progress = (viewport - top) / Math.max(viewport * (1 - endRatio), 1);
      element.style.setProperty("--enter", Math.min(1, Math.max(0, progress)).toFixed(4));
    };
    const schedule = () => {
      if (active && !frame) frame = window.requestAnimationFrame(measure);
    };
    const observer = new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active) schedule();
    }, { rootMargin: "20% 0px" });
    observer.observe(element);
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [endRatio]);

  return ref;
}
